import React, { useEffect, useState } from 'react';
import { getCapitalLedger } from '../../services/SettingsService';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';

const RESERVE_COLOR = '#198754';   // dinheiro em repouso (candidato a rendimento)
const FREE_PIZZA_COLOR = '#ced4da'; // dentro da pizza, ainda não alocado por estratégia
const FUT_MARGIN_COLOR = '#e8590c'; // futuros: margem em uso
const FUT_FREE_COLOR = '#ffd8a8';   // futuros: saldo livre
const LOCKED_COLOR = '#fab005';     // capital real: travado em ordens abertas (onOrder)
const EARN_COLOR = '#12b886';       // capital real: rendendo no Simple Earn
// Paleta para as fatias por estratégia (distintas entre si).
const STRAT_COLORS = ['#0d6efd', '#6610f2', '#d63384', '#fd7e14', '#20c997', '#0dcaf0', '#ffc107', '#6f42c1', '#2b8a3e', '#c92a2a'];

const fmt = (v, d = 2) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

// Donut SVG puro (sem dependência). segments = [{ label, value, color }].
function Donut({ segments, total, quote, caption }) {
    const r = 70, cx = 90, cy = 90, sw = 26;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    return (
        <svg width='180' height='180' viewBox='0 0 180 180'>
            <circle cx={cx} cy={cy} r={r} fill='none' stroke='#e9ecef' strokeWidth={sw} />
            {total > 0 && segments.filter(s => s.value > 0).map((s, i) => {
                const len = (s.value / total) * circ;
                const el = (
                    <circle key={i} cx={cx} cy={cy} r={r} fill='none' stroke={s.color} strokeWidth={sw}
                        strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset}
                        transform={`rotate(-90 ${cx} ${cy})`} />
                );
                offset += len;
                return el;
            })}
            <text x={cx} y={cy - 4} textAnchor='middle' className='fw-bold' style={{ fontSize: 15 }}>{fmt(total, 0)}</text>
            <text x={cx} y={cy + 14} textAnchor='middle' className='text-muted' style={{ fontSize: 10 }}>{quote} {caption}</text>
        </svg>
    );
}

function CapitalPizza({ exposurePercent }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [view, setView] = useState('spot'); // 'spot' | 'global'

    // Reage à troca de ambiente (prod ↔ testnet) — refaz a busca, igual Orders/Dashboard.
    useEffect(() => subscribeToCurrentEnvironment(setEnvironment), []);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getCapitalLedger(environment || 'production')
            .then(d => { if (alive) { setData(d); setLoading(false); } })
            .catch(() => { if (alive) { setData(null); setLoading(false); } });
        return () => { alive = false; };
    }, [environment]);

    if (loading) return null;
    const spot = data?.ledger?.spot;
    const futures = data?.ledger?.futures;
    const strategies = data?.ledger?.strategies || [];
    const quote = data?.quote || 'USDT';

    // Sem saldo (corretora não respondeu / sem credencial) → não monta a pizza.
    if (!spot || spot.walletEstimate == null) {
        return (
            <div className='card border-0 shadow-sm mb-3'>
                <div className='card-body'>
                    <h6 className='fw-bold mb-1'>👛 Carteira por estratégia</h6>
                    <small className='text-muted'>Indisponível no momento (sem saldo da corretora no ambiente atual).</small>
                </div>
            </div>
        );
    }

    // Recalcula a divisão pizza/reserva CLIENT-SIDE a partir do exposure% atual (vem do slider da
    // tela de risco via prop) — o donut reage ao vivo sem refetch. walletEstimate não depende do teto.
    const wallet = spot.walletEstimate;
    const effExp = Math.max(1, Math.min(Number(exposurePercent != null && exposurePercent !== '' ? exposurePercent : spot.exposurePercent) || 70, 99));
    const pizza = wallet * effExp / 100;
    const reserve = wallet - pizza;

    // Fatias por estratégia (uso REAL = spotDeployed). "Livre na carteira" = teto não preenchido.
    const deployedStrats = strategies
        .filter(s => Number(s.spotDeployed) > 0)
        .sort((a, b) => Number(b.spotDeployed) - Number(a.spotDeployed));
    const totalDeployed = deployedStrats.reduce((s, st) => s + Number(st.spotDeployed), 0);
    const freeInPizza = Math.max(0, pizza - totalDeployed);
    const overPizza = totalDeployed > pizza + 0.01;

    const spotStrategySegments = deployedStrats.map((st, i) => ({
        label: st.name || st.symbol || `Estratégia ${st.id}`,
        value: Number(st.spotDeployed),
        color: STRAT_COLORS[i % STRAT_COLORS.length],
    }));

    // Bloco de futuros (carteira separada) — usado no modo global.
    const futMargin = Number(futures?.totalMargin) || 0;
    const futWallet = futures?.walletBalance != null ? Number(futures.walletBalance) : null;
    const futFree = futWallet != null ? Math.max(0, futWallet - futMargin)
        : (futures?.walletAvailable != null ? Number(futures.walletAvailable) : 0);
    const hasFutures = futMargin > 0 || futWallet != null || (futures?.walletAvailable != null);

    const real = data?.ledger?.realAllocation;

    let segments, total, caption, subtitle;
    if (view === 'real' && real) {
        // Pizza do CAPITAL REAL: onde cada parte está FISICAMENTE — posições por estratégia + livre +
        // travado em ordens abertas (onOrder) + Earn + margem de futuros. Resolve o capital que "sumia"
        // da visão por estratégia (que só conta livre + posições preenchidas).
        segments = [
            ...spotStrategySegments,
            { label: 'Livre', value: Number(real.free) || 0, color: FREE_PIZZA_COLOR },
            { label: 'Travado em ordens', value: Number(real.lockedInOrders) || 0, color: LOCKED_COLOR },
            { label: 'Rendimento (Earn)', value: Number(real.earn) || 0, color: EARN_COLOR },
            { label: 'Futuros — margem', value: Number(real.futuresMargin) || 0, color: FUT_MARGIN_COLOR },
        ];
        total = Number(real.total) || 0;
        caption = 'capital real';
        subtitle = <>Onde seu capital está <strong>fisicamente</strong> agora: posições por estratégia, <strong>livre</strong>, <strong>travado em ordens</strong> abertas, rendendo no <strong>Earn</strong> e margem de futuros.</>;
    } else if (view === 'global' && hasFutures) {
        // Pizza GLOBAL: carteira spot (estratégias + livre na pizza + reserva) + carteira de futuros
        // (margem em uso + livre). Visão de portfólio combinada (mercado = 1º corte da árvore).
        segments = [
            ...spotStrategySegments,
            { label: 'Livre na carteira (spot)', value: freeInPizza, color: FREE_PIZZA_COLOR },
            { label: 'Reserva (repouso)', value: reserve, color: RESERVE_COLOR },
            { label: 'Futuros — margem em uso', value: futMargin, color: FUT_MARGIN_COLOR },
            { label: 'Futuros — livre', value: futFree, color: FUT_FREE_COLOR },
        ];
        total = wallet + (futWallet != null ? futWallet : futMargin + futFree);
        caption = 'global';
        subtitle = <>Portfólio combinado <strong>spot + futuros</strong>. Cada estratégia ocupa sua fatia da carteira spot; a carteira de futuros entra com margem em uso e saldo livre.</>;
    } else {
        // Pizza SPOT subdividida por estratégia.
        segments = [
            ...spotStrategySegments,
            { label: 'Livre na carteira', value: freeInPizza, color: FREE_PIZZA_COLOR },
            { label: 'Reserva (repouso)', value: reserve, color: RESERVE_COLOR },
        ];
        total = wallet;
        caption = 'carteira';
        subtitle = <>A <strong style={{ color: RESERVE_COLOR }}>reserva (repouso)</strong> e as <strong>estratégias</strong> dividem o capital, com teto de {effExp}% pras estratégias. Cada estratégia ativa ocupa a sua fatia.</>;
    }

    return (
        <div className='card border-0 shadow-sm mb-3'>
            <div className='card-body'>
                <div className='d-flex justify-content-between align-items-start mb-1'>
                    <h6 className='fw-bold mb-0'>👛 Divisão da carteira <small className='text-muted'>({view === 'real' ? 'capital real' : (view === 'global' && hasFutures ? 'global' : 'spot')})</small></h6>
                    <div className='btn-group btn-group-sm' role='group' aria-label='Visão da carteira'>
                        <button type='button' className={`btn btn-sm ${view === 'spot' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setView('spot')}>Estratégias</button>
                        <button type='button' className={`btn btn-sm ${view === 'real' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setView('real')} title='Onde o capital está fisicamente (livre, ordens, Earn, futuros)'>Capital real</button>
                        {hasFutures && (
                            <button type='button' className={`btn btn-sm ${view === 'global' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setView('global')}>Global</button>
                        )}
                    </div>
                </div>
                <small className='text-muted d-block mb-2'>{subtitle}</small>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                    <Donut segments={segments} total={total} quote={quote} caption={caption} />
                    <div style={{ minWidth: 240, maxWidth: 440, flex: '1 1 320px' }}>
                        {segments.filter(s => s.value > 0).map((s, i) => (
                            <div key={i} className='d-flex align-items-center justify-content-between small mb-1'>
                                <span className='text-truncate' style={{ maxWidth: 180 }}>
                                    <span className='d-inline-block me-2' style={{ width: 10, height: 10, background: s.color, borderRadius: 2 }} />
                                    {s.label}
                                </span>
                                <span className='text-muted ms-2'>{fmt(s.value)} {quote} <span className='text-secondary'>({fmt((s.value / total) * 100, 0)}%)</span></span>
                            </div>
                        ))}
                    </div>
                </div>
                {overPizza && (
                    <div className='alert alert-warning border-0 mt-2 mb-0 py-2 px-3 small'>
                        ⚠ As estratégias spot ({fmt(totalDeployed)} {quote}) já passaram do teto de exposição ({fmt(pizza)} {quote}). Aumente o teto de exposição ou reduza posições.
                    </div>
                )}
                {view === 'spot' && hasFutures && (futures.totalMargin > 0 || futures.walletBalance != null) && (
                    <div className='alert alert-light border mt-2 mb-0 py-2 px-3 small'>
                        <strong>⚡ Futuros</strong> <span className='text-muted'>(carteira separada — veja “Global”)</span> — margem em uso:
                        <strong> {fmt(futures.totalMargin)} {quote}</strong>
                        {futures.walletAvailable != null && <> · disponível: {fmt(futures.walletAvailable)} {quote}</>}
                        {futures.walletBalance != null && <> · saldo: {fmt(futures.walletBalance)} {quote}</>}
                    </div>
                )}
            </div>
        </div>
    );
}

export default CapitalPizza;
